// « Télécharger la feuille de test d'alignement » — the 1-sheet duplex test the
// PDF builder exports from lib/couponSheet.ts as generateCouponAlignmentPdf(settings).
//
// Loaded through import.meta.glob so the page builds (and the button says so)
// even before that file lands: an empty glob = not available yet. When the file
// exists, Vite bundles it as its own lazy chunk, like a normal import().
import type { AppSettings } from '@/lib/api';

type AlignmentModule = { generateCouponAlignmentPdf?: (settings: AppSettings) => unknown };

const loaders = import.meta.glob<AlignmentModule>('/src/lib/couponSheet.ts');

export function alignmentPdfAvailable(): boolean {
  return Object.keys(loaders).length > 0;
}

/** What the generator may hand back, as bytes to save (null = it saved the file itself). */
export async function toPdfBlob(out: unknown): Promise<Blob | null> {
  if (out == null) return null;
  if (out instanceof Blob) return out;
  if (out instanceof Uint8Array) return new Blob([out as BlobPart], { type: 'application/pdf' });
  if (out instanceof ArrayBuffer) return new Blob([out], { type: 'application/pdf' });
  if (typeof out === 'object' && out && 'bytes' in out) return toPdfBlob((out as { bytes: unknown }).bytes);
  if (typeof out === 'object' && out && 'blob' in out) return toPdfBlob((out as { blob: unknown }).blob);
  return null;
}

export async function downloadAlignmentPdf(settings: AppSettings): Promise<void> {
  const load = Object.values(loaders)[0];
  if (!load) throw new Error('La feuille de test n’est pas encore disponible dans cette version.');
  const mod = await load();
  if (typeof mod.generateCouponAlignmentPdf !== 'function') {
    throw new Error('La feuille de test n’est pas encore disponible dans cette version.');
  }
  const blob = await toPdfBlob(await mod.generateCouponAlignmentPdf(settings));
  if (!blob) return; // the generator downloaded it itself
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'storiz-coupons-test-alignement.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
